import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@aimsos/ui';
import {
  getAgentTools,
  updateAgentTools,
  removeToolFromAgent,
  getToolsList,
} from '../../../../services/tools.js';

/**
 * @param {{ agentId: string | undefined, agentLoading: boolean, t: (k: string, o?: object) => string }} params
 */
const useToolsPage = ({ agentId, agentLoading, t }) => {
  const toast = useToast();

  const [installedTools, setInstalledTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingToolsList, setLoadingToolsList] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [showSlideoutTools, setShowSlideoutTools] = useState(false);
  const [showConfigurationSlideout, setShowConfigurationSlideout] = useState(false);
  const [selectedToolForConfiguration, setSelectedToolForConfiguration] = useState(null);
  /** Configure sub-view inside the tools library drawer (same shell drawer, not a second Drawer). */
  const [libraryDrawerConfigTool, setLibraryDrawerConfigTool] = useState(null);
  /** Pending settings for tools not yet installed — applied when the tool is added. */
  const [pendingToolConfigs, setPendingToolConfigs] = useState({});

  const showNotification = useCallback(
    (messageKey, type = 'success') => {
      const message = t(messageKey);
      if (type === 'error') toast.error({ message });
      else toast.success({ message });
    },
    [toast, t],
  );

  const fetchAgentTools = useCallback(async () => {
    if (!agentId || agentLoading) return;

    setLoading(true);
    try {
      const response = await getAgentTools(agentId);
      const tools = response?.data?.data || [];
      setInstalledTools(Array.isArray(tools) ? tools : []);
    } catch (err) {
      console.error('Error fetching agent tools:', err);
      setInstalledTools([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, agentLoading]);

  const fetchToolsList = useCallback(async () => {
    setLoadingToolsList(true);
    try {
      const response = await getToolsList();
      const tools = response?.data?.data || [];
      setAvailableTools(Array.isArray(tools) ? tools : []);
    } catch (err) {
      console.error('Error fetching tools list:', err);
      setAvailableTools([]);
    } finally {
      setLoadingToolsList(false);
    }
  }, []);

  useEffect(() => {
    if (agentId && !agentLoading) {
      fetchAgentTools();
    }
  }, [agentId, agentLoading, fetchAgentTools]);

  useEffect(() => {
    if (showSlideoutTools && availableTools.length === 0) {
      fetchToolsList();
    }
  }, [showSlideoutTools, availableTools.length, fetchToolsList]);

  const handleToggleTool = useCallback(
    async (toolId) => {
      if (!agentId || isUpdating) return;

      setIsUpdating(true);
      try {
        const updatedTools = installedTools.map((tool) =>
          tool.id === toolId ? { ...tool, isEnabled: !tool.isEnabled } : tool,
        );
        setInstalledTools(updatedTools);
        await updateAgentTools(agentId, { tools: updatedTools });
        showNotification('edit.tools.toastUpdated', 'success');
      } catch (err) {
        console.error('Error toggling tool:', err);
        fetchAgentTools();
        showNotification('edit.tools.toastUpdateError', 'error');
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, installedTools, isUpdating, fetchAgentTools, showNotification],
  );

  const handleDeleteTool = useCallback(
    async (toolId) => {
      if (!agentId || isUpdating) return;

      setIsUpdating(true);
      try {
        const updatedTools = installedTools.filter((tool) => tool.id !== toolId);
        setInstalledTools(updatedTools);
        await removeToolFromAgent(agentId, toolId);
        showNotification('edit.tools.toastRemoved', 'success');
      } catch (err) {
        console.error('Error removing tool:', err);
        fetchAgentTools();
        showNotification('edit.tools.toastRemoveError', 'error');
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, installedTools, isUpdating, fetchAgentTools, showNotification],
  );

  const handleAddTools = useCallback(
    async (newTools) => {
      if (!agentId || !newTools?.length || isUpdating) return;

      setIsUpdating(true);
      try {
        const toolsToAdd = newTools.map((tool) => {
          const id = tool.id || tool.action_uuid || tool.tool_id;
          return {
            id,
            title: tool.title || tool.name,
            description: tool.description || '',
            icon: tool.icon || '',
            isEnabled: true,
            tag: tool.tag || 'Ready to use',
            tagColor: tool.tagColor || 'informative',
            settings: { ...(tool.settings || {}), ...(pendingToolConfigs[id] || {}) },
            type: tool.type || '',
            category: tool.category || tool.categories || '',
          };
        });

        const updatedTools = [...installedTools, ...toolsToAdd];
        setInstalledTools(updatedTools);
        await updateAgentTools(agentId, { tools: updatedTools });
        setShowSlideoutTools(false);
        setLibraryDrawerConfigTool(null);
        setPendingToolConfigs((prev) => {
          const next = { ...prev };
          toolsToAdd.forEach((t) => delete next[t.id]);
          return next;
        });
        showNotification('edit.tools.toastAdded', 'success');
      } catch (err) {
        console.error('Error adding tools:', err);
        fetchAgentTools();
        showNotification('edit.tools.toastAddError', 'error');
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, installedTools, isUpdating, pendingToolConfigs, fetchAgentTools, showNotification],
  );

  const handleUpdateToolSettings = useCallback(
    async (toolId, settings) => {
      if (!agentId || isUpdating) return;

      setIsUpdating(true);
      try {
        const updatedTools = installedTools.map((tool) =>
          tool.id === toolId ? { ...tool, settings: { ...tool.settings, ...settings } } : tool,
        );
        setInstalledTools(updatedTools);
        await updateAgentTools(agentId, { tools: updatedTools });
        setShowConfigurationSlideout(false);
        setSelectedToolForConfiguration(null);
        showNotification('edit.tools.toastConfigSaved', 'success');
      } catch (err) {
        console.error('Error updating tool settings:', err);
        fetchAgentTools();
        showNotification('edit.tools.toastConfigError', 'error');
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, installedTools, isUpdating, fetchAgentTools, showNotification],
  );

  const handleOpenSlideoutTools = useCallback(() => setShowSlideoutTools(true), []);
  const handleCloseSlideoutTools = useCallback(() => {
    setShowSlideoutTools(false);
    setLibraryDrawerConfigTool(null);
  }, []);

  const handleOpenConfiguration = useCallback((tool, fromToolsLibrary = false) => {
    if (fromToolsLibrary) {
      setLibraryDrawerConfigTool(tool);
      return;
    }
    setSelectedToolForConfiguration(tool);
    setShowConfigurationSlideout(true);
  }, []);

  const handleCloseConfiguration = useCallback(() => {
    setShowConfigurationSlideout(false);
    setSelectedToolForConfiguration(null);
  }, []);

  const handleGoBackFromLibraryConfigure = useCallback(() => {
    setLibraryDrawerConfigTool(null);
  }, []);

  const handleSaveLibraryDrawerConfiguration = useCallback(
    async (tool, configurationData) => {
      if (!tool?.id) {
        setLibraryDrawerConfigTool(null);
        return;
      }
      const isInstalled = installedTools.some((x) => x.id === tool.id);
      if (isInstalled) {
        await handleUpdateToolSettings(tool.id, configurationData);
      } else {
        setPendingToolConfigs((prev) => ({ ...prev, [tool.id]: configurationData }));
        showNotification('edit.tools.toastConfigSaved', 'success');
      }
      setLibraryDrawerConfigTool(null);
    },
    [installedTools, handleUpdateToolSettings, showNotification],
  );

  const handleSaveConfiguration = useCallback(
    async (configurationData) => {
      if (selectedToolForConfiguration?.id) {
        await handleUpdateToolSettings(selectedToolForConfiguration.id, configurationData);
      }
    },
    [selectedToolForConfiguration, handleUpdateToolSettings],
  );

  return {
    installedTools,
    availableTools,
    loading,
    loadingToolsList,
    isUpdating,
    showSlideoutTools,
    showConfigurationSlideout,
    selectedToolForConfiguration,
    libraryDrawerConfigTool,
    handleToggleTool,
    handleDeleteTool,
    handleAddTools,
    handleOpenSlideoutTools,
    handleCloseSlideoutTools,
    handleOpenConfiguration,
    handleCloseConfiguration,
    handleGoBackFromLibraryConfigure,
    handleSaveLibraryDrawerConfiguration,
    handleSaveConfiguration,
  };
};

export default useToolsPage;
